import {Container} from "pixi.js";
import {MdFrameBlock, MdFrameBlockChild, MdFrameItem, MdFrameStick, stepVoids} from "../../model/frame/MdFrameBlock";
import {FrmVar} from "../../drawfrm/FrmVar";
import {ElevationApp} from "../../ElevationApp";
import {MdFrameSpec} from "../../model/frame/MdFrameSpec";
import {StickMaterial, StickResize} from "./StickMaterial";
import {DRAG_OK, DragStatus, makePerimeterResize, MIN_BLOCK, stickThicknessVar, VOID_ZERO} from "./Util";
import {absorbSizeDelta, beginTallEndEdit, canGrowPerimeterPiece, finishTallEndEdit, drawnStick, givePieceOwnStick, growSplitPart, isDoorCell, removeStep, resizeSplitCell, SplitGrow, startSplitGrow, stepOutlinePiece, TallEndEdit} from "./BlockOps";
import {inches} from "../../const";
import {FrameBlockInnerDimension} from "./FrameBlockInnerDimension";
import type {FrameBlockItem} from "./FrameBlockItem";
import {crossThickness, pieceSlope, slopeAt, slopeDepthAt} from "./Slope";
import type {FrameGridView} from "./FrameGridScene";
import {Disposable} from "../../drawfrm/DrawFrmThing"

export type LayoutRect = {x: number; y: number; width: number; height: number};

export type TFrameBlock = {
    getWidth(strict: boolean): number;
    getHeight(strict: boolean): number;
    reposition(refX: number, refY: number): void;
    getItems(): any[];

    destroy(): void;

    model: MdFrameBlock | MdFrameItem;

    kiwiWidth: any;
    width?: FrmVar;

    kiwiHeight: any;
    height?: FrmVar;

    // where this node landed in the last reposition (scene coords). The root's outline
    // renderer reads these to size/segment the outline sticks.
    layoutRect?: LayoutRect;

    // sticks declared at the very start / end of a block's `children` — i.e. on a
    // block edge rather than between two cells: the block's own edge sticks, on the
    // frame outline. The block gives them room in its flow; the root draws them
    // (renderOutline). undefined on items.
    leadingEdgeStick?: MdFrameStick;
    trailingEdgeStick?: MdFrameStick;

    renderOutline(spec: MdFrameSpec): void;

    // the visible outline stick pieces with the edge each lies on (empty on leaves and
    // on blocks other than the root).
    outlineMats(): {mat: StickMaterial; side: EdgeSide}[];

    // append the leaf cells along `side` of this subtree, in order along the edge,
    // each with the stick that covers its edge there. `cover` is the stick covering
    // this subtree's edge so far; a block's own edge stick on that side replaces it.
    collectEdgeLeaves(side: EdgeSide, cover: EdgeCover, out: EdgeLeaf[]): void;

    // split the interior sticks of this subtree where a void lies beside them (see
    // FrameBlockExt.splitAtVoids). Leaf items have none.
    layoutVoidParts(): void;

    // the slopes of the sloped sticks over a leaf cell, set by renderOutline each
    // render: begin clears them, set records one side, end applies them (a leaf
    // redraws when they changed). Blocks pass them down to their leaves.
    beginEdgeSlopes(): void;
    setEdgeSlope(side: EdgeSide, slope: EdgeSlope): void;
    endEdgeSlopes(): void;

    // where a dimension of `axis` over this node goes when a sloped stick slopes across
    // it: the cross position (node coords) of the stick's tall end, when it lies on this
    // node. Null when no slope ends here. Blocks have none; their leaves do.
    stickTallEndCross(axis: "width" | "height"): number | null;
}

// how far a sloped stick cuts into a cell on one side: the depth at the cell's
// start and end along that side (x for top/bottom, y for left/right).
export type EdgeSlope = {start: number; end: number};

export type EdgeSide = "top" | "bottom" | "left" | "right";

const EDGE_SIDES: EdgeSide[] = ["top", "bottom", "left", "right"];

// the stick covering a part of the outline, and the block whose own edge stick it is
// (the block that gave it room). null where no stick does: a void, or the threshold.
export type EdgeCover = {stick: MdFrameStick; owner: FrameBlockExt} | null;

// one leaf cell along an outline edge, with the stick that covers its edge there.
export type EdgeLeaf = {cell: TFrameBlock; cover: EdgeCover};

// one drawn piece of an outline edge: a run of neighbouring leaves covered by the
// same stick, as an along-edge range.
type EdgePiece = {stick: MdFrameStick; owner: FrameBlockExt; min: number; max: number; leaves: TFrameBlock[]};

// an outline stick piece drawn on one edge, created the first time a render needs it.
// `leafIds` / `steppable` are refreshed each render for the drag (see outlineResize).
type PerimSlot = {
    mat: StickMaterial;
    length: FrmVar;
    stick: MdFrameStick;
    side: EdgeSide;
    leafIds: string[];
    steppable: boolean;
};

// an interior stick between two cells. Beside a void it is drawn in several pieces
// (parts[0] is `stick`); makePart builds one more piece on demand.
export type InteriorStick = {
    stick: StickMaterial;
    length: FrmVar;
    thickness: FrmVar;
    parts: {mat: StickMaterial; length: FrmVar}[];
    makePart: () => {mat: StickMaterial; length: FrmVar};
};

export abstract class FrameBlockExt {

    // implemented by FrameBlockRow / FrameBlockColumn
    abstract getWidth(strict: boolean): number;
    abstract getHeight(strict: boolean): number;
    abstract reposition(refX: number, refY: number): void;

    private _destroyed: boolean = false
    private disposables: Disposable[] = [];

    model: MdFrameBlock;
    scene: any;
    parent?: FrameBlockExt;

    elev: ElevationApp;

    kiwiWidth: any;
    width: FrmVar;

    kiwiHeight: any;
    height: FrmVar;

    layoutRect?: LayoutRect;

    leadingEdgeStick?: MdFrameStick;
    trailingEdgeStick?: MdFrameStick;

    // initialized in child classes
    children: TFrameBlock[] = [];

    // a block has no rect of its own, but when it carries an explicit size we draw
    // an inside dimension for it (like a leaf item does). The annotations live in
    // this container, positioned at the block's top-left during layout.
    dimensionContainer: Container;
    private innerDimension: FrameBlockInnerDimension;

    // a number edit under a sloped stick, finished by relayout once the layout has run.
    private tallEndEdit: TallEndEdit | null = null;

    // outline stick pieces by `${side}|${stick id}|${occurrence}`, created by
    // renderOutline() (on the root) as the edges need them and hidden when unused.
    private perim = new Map<string, PerimSlot>();

    constructor(scene: any, model: MdFrameBlock, parent?: any) {
        this.model = model;
        this.scene = scene;
        this.elev = scene.elev;
        this.parent = parent;

        this.width = new FrmVar(model.id + ":width", 0);
        this.height = new FrmVar(model.id + ":height", 0);

        if (parent && parent.model.kind === "column" && !!model.size) {
            this.height = model.size;
        } else if (parent && parent.model.kind === "row" && !!model.size) {
            this.width = model.size;
        }

        // attach the dimension container to the same layer the cells render into,
        // and track it so a grid rebuild tears it down.
        this.dimensionContainer = new Container();
        scene.parentThing.lines.addChild(this.dimensionContainer);

        // a block only owns a dimension on its parent-flow axis (width inside a row,
        // height inside a column — see hasWidth/HeightDimension). Gate the axes here
        // so an axis it doesn't own is never drawn; otherwise the block paints a
        // phantom dimension (backed by its own pinned size var) over the leaf item's
        // dimension for the same cell — two "unlinked" dimensions on one face.
        this.innerDimension = this.track(new FrameBlockInnerDimension(this, {
            axes: {width: this.hasWidthDimension(), height: this.hasHeightDimension()},
        }));
    }

    // where this block's own dimension lines sit (see FrameBlockInnerDimension.lineInsets).
    dimensionLineInsets(): {top: number; left: number} {
        return this.innerDimension.lineInsets();
    }

    // InnerDimensionTarget plumbing — `scene` is the FrameGridView.
    get view(): FrameGridView {
        return this.scene;
    }

    // a block is dimensioned on its parent-flow axis (the axis its size lives on):
    // width inside a row, height inside a column. The root (no parent) and the
    // cross axis carry no editable dimension. The door cell's width is the door's
    // (dictated), dimensioned by DoorNominalDimensions. A block whose cells already
    // read that extent (see cellsDimension) skips it too: its line would run along
    // theirs at the same inset and read as one dimension spanning them.
    hasWidthDimension(): boolean {
        return this.parent?.model.kind === "row" && !isDoorCell(this.model) && !cellsDimension(this.model, "width");
    }

    // a door-bearing block additionally skips the height dimension: its height follows
    // the door (and the frame's undercut), not a size the user should read or edit —
    // the door's real height dimension is drawn by DoorFace (see drawDoorHeight).
    hasHeightDimension(): boolean {
        return this.parent?.model.kind === "column" && !modelHasDoor(this.model) && !cellsDimension(this.model, "height");
    }

    // a block alone in a split part of the frame edge resizes through a step, so the
    // parts beside it keep their size (see resizeSplitCell). Any other block-size edit
    // scales the cells inside with it, so they keep their proportions.
    onDimensionEdit(axis: "width" | "height", delta: number): boolean | void {
        const spec = this.elev.frameSpec;

        const tallEnd = beginTallEndEdit(spec, this.model.id, axis, axis === "height" ? this.height : this.width, delta);
        if(tallEnd === "refused") {
            this.elev.draw.showError("The short end of the slope must be at least 3\".");
            return false;
        }
        if(tallEnd) delta = tallEnd.delta;

        const result = resizeSplitCell(spec, this.model.id, delta, undefined, axis);
        if(result === "refused") {
            this.elev.draw.showError("Can't step the frame for this size.");
            return false;
        }

        this.tallEndEdit = tallEnd?.edit ?? null;
        if(result === "rebuilt") {
            spec.assignLiteMarks();
            spec.frameBlocksChanged.emit(true);
            return;
        }
        if(result === "changed") return;

        absorbSizeDelta(spec, this.model, axis, delta);
    }

    // re-flow the grid after a block-size dimension edit, then nudge the scene
    // layout to re-fit to the grid's new bounds.
    relayout() {
        this.scene.render();
        const tallEnd = this.tallEndEdit;
        this.tallEndEdit = null;
        if(tallEnd && finishTallEndEdit(this.elev.frameSpec, tallEnd)) this.scene.render();
        this.scene.parentThing.changed.emit("re-layout");
    }

    getItems() {
        let list: any[] = [];

        this.children.forEach(c => {
            list.push(...c.getItems());
        });

        return list;
    }

    // record where this block landed; child classes call this from reposition().
    protected setLayoutRect(x: number, y: number) {
        if(this._destroyed) return;

        this.layoutRect = {x, y, width: this.getWidth(true), height: this.getHeight(true)};

        // move the dimension annotations to the block's top-left and redraw them
        // (a no-op unless this block has an editable, non-calc size).
        this.dimensionContainer.position.set(x, y);

        // the cell fills are added to this same layer during construction — i.e.
        // after this block's container — so they'd paint over the dimension. Lift
        // it back to the front so it stays readable above the cells it spans.
        if(this.hasWidthDimension() || this.hasHeightDimension()) {
            this.scene.parentThing.lines.addChild(this.dimensionContainer);
        }

        this.innerDimension.draw();
    }

    // ---- outline -----------------------------------------------------------

    // the thickness an edge stick takes in this block's flow (0 without one). A
    // sloped stick takes more: its stock lies along the slope (see crossThickness).
    // The slope needs this block's extent along the edge, from the last layout.
    protected edgeThickness(stick?: MdFrameStick): number {
        if(!stick) return 0;
        const t = stickThicknessVar(stick, this.elev.frameSpec).get();
        if(!stick.slope || !this.layoutRect) return t;
        const len = this.model.kind === "row" ? this.layoutRect.height : this.layoutRect.width;
        return crossThickness(t, len, {a: 0, b: stick.slope.drop.get()});
    }

    // see TFrameBlock: a block has no slopes of its own; its leaves hold them.
    beginEdgeSlopes() {
        for(const c of this.children) c.beginEdgeSlopes();
    }

    setEdgeSlope(side: EdgeSide, slope: EdgeSlope) {}

    endEdgeSlopes() {
        for(const c of this.children) c.endEdgeSlopes();
        this.placeDimensionAtTallEnd();
    }

    stickTallEndCross(axis: "width" | "height"): number | null {
        return null;
    }

    // this block's own dimension on an axis a sloped stick slopes across moves to the
    // stick's tall end, where the block has its full size (its leaf there reports the
    // end). Back to the usual inset when no slope ends on the block.
    private placeDimensionAtTallEnd() {
        if(!this.layoutRect) return;
        for(const axis of ["width", "height"] as const) {
            const owns = axis === "width" ? this.hasWidthDimension() : this.hasHeightDimension();
            if(!owns) continue;

            const items = this.getItems() as FrameBlockItem[];
            let cross: number | null = null;
            for(const item of items) {
                const tall = item.stickTallEndCross(axis);
                if(tall === null || !item.layoutRect) continue;
                cross = axis === "height"
                    ? item.layoutRect.x - this.layoutRect.x + tall
                    : item.layoutRect.y - this.layoutRect.y + tall;
            }
            if(!this.innerDimension.setCross(axis, cross)) continue;
            this.innerDimension.draw();
            // the leaves keep their labels clear of this line (see FrameBlockItem.dimensionLines).
            for(const item of items) item.draw();
        }
    }

    // the flow space both edge sticks take together.
    protected edgeThicknessTotal(): number {
        return this.edgeThickness(this.leadingEdgeStick) + this.edgeThickness(this.trailingEdgeStick);
    }

    // how far an interior stick between the cells `lead` and `trail` stops short of this
    // block's edge on `side`: when both cells carry an edge stick at the corner the
    // interior stick meets, it butts into that continuous jamb/rail instead of crossing
    // it (the jambs beside a transom bar; the sill runs under a mullion between two
    // lites). When either corner has none (the door's open sill, a step), the stick
    // runs on to the block's edge — unless the stick covering this block there from a
    // block above slopes: then it stops at that slope's inner face. Under a sloped stick
    // the stop is the deepest point of the stick's inner face across the interior
    // stick's thickness (`cross`, the interior stick's extent across its length).
    protected interiorTrim(lead: TFrameBlock, trail: TFrameBlock, side: EdgeSide, cross: {min: number; max: number}): number {
        const a = cornerCover(lead, side, "trailing");
        const b = cornerCover(trail, side, "leading");
        const spec = this.elev.frameSpec;

        if(a && b) {
            const depth = (cover: {stick: MdFrameStick; owner: FrameBlockExt}) => {
                const t = stickThicknessVar(cover.stick, spec).get();
                const span = alongEdge(side, cover.owner.layoutRect);
                if(!cover.stick.slope || !span) return t;
                return Math.max(slopeDepthAt(cover.stick, side, span, t, cross.min), slopeDepthAt(cover.stick, side, span, t, cross.max));
            };
            return Math.max(depth(a), depth(b));
        }

        // this block's edge lies at the slot of the stick above; the slope's inner face
        // lies past it by the slope's offset there.
        const above = this.coverAbove(side);
        const span = above?.stick.slope ? alongEdge(side, above.owner.layoutRect) : null;
        if(!above || !span) return 0;
        const full = pieceSlope(above.stick, side, span, span);
        const offset = (x: number) => Math.abs(slopeAt(full, span.max - span.min, x - span.min));
        return Math.max(offset(cross.min), offset(cross.max));
    }

    // the stick covering this block's edge on `side`: its own edge stick there, else the
    // nearest block above it with one, as long as this block reaches that side of it.
    private coverAbove(side: EdgeSide): EdgeCover {
        let block: FrameBlockExt | undefined = this;
        while(block) {
            const own = block.ownEdgeStick(side);
            if(own) return {stick: drawnStick(this.elev.frameSpec, own), owner: block};
            const parent: FrameBlockExt | undefined = block.parent;
            if(parent && !parent.cellsOnSide(side).includes(block)) return null;
            block = parent;
        }
        return null;
    }

    // the cell an outline edge drag resizes (on its flow size): the edge cell the stick
    // abuts. For an edge across a block's flow that's its leading/trailing cell; for an
    // edge along the flow, descend single-cell wrappers to the block stacking against
    // the edge. With several cells side-on, follow the one holding the door: the door's
    // row fixes the frame's size there, and the others follow it. An edge cell holding
    // the door is descended into the same way. Returned with the blocks descended
    // through to reach it, this block first. Null when the edge ends at the door
    // itself, or no cell can be found.
    private outlineResizePath(side: EdgeSide): {cell: TFrameBlock; path: FrameBlockExt[]} | null {
        const dragHorizontal = side === "left" || side === "right";
        const leading = side === "top" || side === "left";

        const path: FrameBlockExt[] = [];
        let block: FrameBlockExt = this;
        for(;;) {
            path.push(block);
            const acrossFlow = (block.model.kind === "row") === dragHorizontal;
            if(!acrossFlow) {
                const next = block.children.length === 1 ? block.children[0] : block.children.find(cellHasDoor);
                if(!(next instanceof FrameBlockExt)) return null;
                block = next;
                continue;
            }

            if(block.children.length === 0) return null;
            const cell = leading ? block.children[0] : block.children[block.children.length - 1];
            if(!cellHasDoor(cell)) return {cell, path};
            if(!(cell instanceof FrameBlockExt)) return null;
            block = cell;
        }
    }

    // an outline drag has moved its cell's flow size from `from` to `to`, which grows
    // the frame by the same amount along `axis`. Every block on the way down to the
    // cell grows with it: one flowing along the axis grows through the cell alone
    // (its other cells keep their size), one flowing across it carries all its cells
    // along, so each of those scales its contents to match (see absorbSizeDelta). The
    // cell's own contents scale with it too.
    private scaleOutlineResize(axis: "width" | "height", target: {cell: TFrameBlock; path: FrameBlockExt[]}, from: number, to: number) {
        const spec = this.elev.frameSpec;
        const delta = to - from;

        for(let i = 0; i < target.path.length; i++) {
            const block = target.path[i];
            const along = (block.model.kind === "row") === (axis === "width");
            if(along) continue;

            const onPath = i + 1 < target.path.length ? target.path[i + 1] : target.cell;
            for(const c of block.children) {
                if(c !== onPath && c instanceof FrameBlockExt) absorbSizeDelta(spec, c.model, axis, delta);
            }
        }

        if(target.cell instanceof FrameBlockExt) absorbSizeDelta(spec, target.cell.model, axis, delta);
    }

    // draw the outline sticks along the frame outline (called on the root): each edge
    // is drawn in pieces, one per run of edge cells covered by the same stick, skipping
    // voids and the threshold. A piece sits in the slot its owner block gave the stick
    // and spans the cells it covers, so the corner boxes stay empty for Terminations.
    // Then record each step's size.
    renderOutline(spec: MdFrameSpec) {
        if(!this.layoutRect) return;

        this.beginEdgeSlopes();
        const used = new Set<string>();
        for(const side of EDGE_SIDES) {
            const leaves = this.edgeLeaves(side);
            const pieces = edgePieces(side, leaves);
            const whole = pieces.length === 1 && leaves.every(l => !!l.cover);
            // the lowest piece on a side reaches the floor unless a step (an uncovered
            // void) lies under it. A jamb over a sill run stops at the run and extends
            // down to the floor at the corner (Terminations), so it reaches it too.
            const lastReachesFloor = !!leaves[leaves.length - 1]?.cover;

            const count = new Map<string, number>();
            pieces.forEach(p => count.set(p.stick.id, (count.get(p.stick.id) ?? 0) + 1));

            const seen = new Map<string, number>();
            for(const p of pieces) {
                const n = seen.get(p.stick.id) ?? 0;
                seen.set(p.stick.id, n + 1);

                const key = `${side}|${p.stick.id}|${n}`;
                used.add(key);

                const slot = this.perimSlot(key, side, p.stick, spec);
                slot.leafIds = p.leaves.map(l => l.model.id);
                // a stick over the whole edge grows the frame when dragged; one over part
                // of it steps.
                slot.steppable = !whole && !p.leaves.some(cellHasDoor);
                slot.mat.modelEnds = {A: n === 0, B: n === count.get(p.stick.id)! - 1};

                const geom = pieceGeom(side, p, spec);
                if(!geom || geom.length <= 0) {
                    slot.mat.graphic.visible = false;
                    continue;
                }

                slot.mat.graphic.visible = true;
                slot.mat.graphic.position.set(geom.x, geom.y);
                slot.length.set(geom.length, {debug: "outline"});

                // a sloped stick: the piece draws its part of the slope, and the cells
                // under it are cut by it.
                const span = alongEdge(side, p.owner.layoutRect)!;
                const full = pieceSlope(p.stick, side, span, span);
                slot.mat.setSlope(pieceSlope(p.stick, side, span, {min: p.min, max: p.max}));
                if(p.stick.slope) {
                    for(const leaf of p.leaves) {
                        const r = alongEdge(side, leaf.layoutRect);
                        if(!r) continue;
                        const depth = (x: number) => Math.abs(slopeAt(full, span.max - span.min, x - span.min));
                        leaf.setEdgeSlope(side, {start: depth(r.min), end: depth(r.max)});
                    }
                }

                // only a jamb piece that reaches the floor is cut down by the undercut.
                const reachesFloor = (side === "left" || side === "right") && p === pieces[pieces.length - 1] && lastReachesFloor;
                slot.mat.setUndercut(reachesFloor ? spec.undercutFrame : null);
            }
        }

        for(const [key, slot] of this.perim) {
            if(!used.has(key)) slot.mat.graphic.visible = false;
        }

        this.endEdgeSlopes();
        this.recordSteps(spec);
    }

    // the visible outline pieces, with their edge.
    outlineMats(): {mat: StickMaterial; side: EdgeSide}[] {
        return Array.from(this.perim.values())
            .filter(s => s.mat.graphic.visible)
            .map(s => ({mat: s.mat, side: s.side}));
    }

    // the floor line: the bottom of this block's rect (the root's is the frame's), in
    // scene coords.
    floorY(): number {
        const r = this.layoutRect;
        if(!r) return 0;
        return r.y + r.height;
    }

    // the leaf cells along one edge of this subtree, each with its covering stick.
    private edgeLeaves(side: EdgeSide): EdgeLeaf[] {
        const out: EdgeLeaf[] = [];
        this.collectEdgeLeaves(side, null, out);
        return out;
    }

    // the drawn piece for `key`, made on first use.
    private perimSlot(key: string, side: EdgeSide, stick: MdFrameStick, spec: MdFrameSpec): PerimSlot {
        const existing = this.perim.get(key);
        if(existing) return existing;

        const length = new FrmVar(`${this.model.id}:perim:${key}:len`, 0);
        const ref: {slot?: PerimSlot} = {};
        const mat = new StickMaterial(this.elev, {
            // use the model stick's id so selection / ops can locate it in the model
            // rather than parsing the id.
            id: stick.id,
            view: this.scene,
            orientation: side === "top" || side === "bottom" ? "horizontal" : "vertical",
            length,
            thickness: stickThicknessVar(stick, spec),
            resize: this.outlineResize(side, () => ref.slot!),
            // one "Cutdown: {dim}" callout on the printout — put it on the first left
            // jamb piece so the export shows it once, not per jamb.
            showCutdown: side === "left" && !Array.from(this.perim.values()).some(s => s.side === "left"),
        });

        const slot: PerimSlot = {mat, length, stick, side, leafIds: [], steppable: false};
        ref.slot = slot;
        this.perim.set(key, slot);
        this.scene.parentThing.add(mat);
        this.scene.registerStick(mat);
        return slot;
    }

    // dragging an outline piece. A piece that covers only part of its edge steps
    // inward: the drag previews the move, and the release puts a void behind the
    // piece (see stepOutlinePiece). A piece that covers the whole edge grows or shrinks
    // the frame instead.
    private outlineResize(side: EdgeSide, slotOf: () => PerimSlot): StickResize {
        const axis: "width" | "height" = side === "left" || side === "right" ? "width" : "height";
        const target = this.outlineResizePath(side);
        const cell = target ? (axis === "width" ? target.cell.width : target.cell.height) ?? null : null;
        const trailing = side === "right" || side === "bottom";
        const status = (s: DragStatus | null) => this.elev.dragStatus.emit(s);
        const onResize = (from: number, to: number) => {
            if(target) this.scaleOutlineResize(axis, target, from, to);
        };
        const min = () => MIN_BLOCK + (slotOf().stick.slope?.drop.get() ?? 0);
        const grow = cell ? makePerimeterResize({cell, sign: trailing ? 1 : -1, relayout: () => this.relayout(), status, onResize, min}) : null;

        // unit vector of an inward move, in scene coords.
        const inward = {
            x: side === "left" ? 1 : side === "right" ? -1 : 0,
            y: side === "top" ? 1 : side === "bottom" ? -1 : 0,
        };

        let stepping = false;
        // a split part's own stick also drags outward: its cell grows (see
        // startSplitGrow).
        let splitGrow: SplitGrow | null = null;
        // a piece over one cell drags outward too: its cell takes a stick of its own
        // first, which then goes on with the drag as a split part.
        let pieceGrows = false;
        let start = {x: 0, y: 0};
        let depth = 0;

        // the layout stays still during the drag (see DrawFrmApp.layoutFrozen), and the
        // frame grows from its top-left. On a top or left edge, move the drawing back
        // by the growth so the far edge stays put and the dragged edge follows the
        // pointer.
        let anchor: {x: number; y: number; width: number; height: number} | null = null;
        const keepFarEdge = () => {
            const r = this.layoutRect;
            const g = this.scene.parentThing.graphic;
            if(!r || !anchor || trailing) return;
            if(side === "top") g.position.y = anchor.y - (r.height - anchor.height) * g.scale.y;
            else g.position.x = anchor.x - (r.width - anchor.width) * g.scale.x;
        };

        return {
            begin: () => {
                const r = this.layoutRect;
                const g = this.scene.parentThing.graphic;
                anchor = r ? {x: g.position.x, y: g.position.y, width: r.width, height: r.height} : null;

                stepping = slotOf().steppable;
                if(!stepping) {
                    grow?.begin();
                    return;
                }

                const pos = slotOf().mat.graphic.position;
                start = {x: pos.x, y: pos.y};
                depth = 0;
                const spec = this.elev.frameSpec;
                splitGrow = startSplitGrow(spec, slotOf().stick.id);
                pieceGrows = !splitGrow && canGrowPerimeterPiece(spec, slotOf().stick.id, side, slotOf().leafIds);
            },
            move: (delta: number) => {
                if(!stepping) {
                    grow?.move(delta);
                    keepFarEdge();
                    return;
                }

                const r = this.layoutRect;
                const max = r ? (inward.x !== 0 ? r.width : r.height) : 0;
                depth = Math.max(splitGrow || pieceGrows ? -Infinity : 0, Math.min(max, trailing ? -delta : delta));

                if(depth < 0 && pieceGrows) {
                    return {commit: () => this.commitOwnStick(slotOf(), side)};
                }

                // the rest of the row already has steps: the part grows or shrinks live
                // both ways, and those steps take the change (no step on this part).
                if(splitGrow?.live) {
                    splitGrow.apply(-depth);
                    this.relayout();
                    keepFarEdge();
                    status(DRAG_OK);
                    return;
                }

                // else the piece previews the move. Outward, once the fresh steps clear
                // VOID_ZERO they are made and the stick takes the drag over; short of
                // that the release changes nothing.
                slotOf().mat.graphic.position.set(start.x + inward.x * depth, start.y + inward.y * depth);

                if(depth < 0 && splitGrow) {
                    const amount = -depth;
                    if(amount >= splitGrow.min) {
                        status(null);
                        return {commit: () => this.commitGrowOut(slotOf(), amount)};
                    }
                    status(DRAG_OK);
                    return;
                }

                // inward past VOID_ZERO, make the step right away and let its stick take
                // the drag over, so the cells around it follow live. The void is the
                // depth: the stick's outer face moves off the outline by it.
                const size = depth;
                if(size > VOID_ZERO) {
                    status(null);
                    return {commit: () => this.commitStep(slotOf(), side, size)};
                }
                status(DRAG_OK);
            },
            end: () => {
                if(!stepping) {
                    grow?.end();
                    return;
                }

                // released outward short of the fresh steps' minimum: no change.
                // Released inside the stick's slot: no step.
                status(null);
                if(splitGrow?.live) splitGrow.finish();
                this.relayout();
            },
        };
    }

    // put a void of `size` behind the dragged piece, so the piece lands where the drag
    // is. Returns the step stick's id (it takes the drag over), or null when the piece
    // can't step. The void may start tiny: the drag goes on, and its release removes a
    // void at or under VOID_ZERO (see makeMullionResize).
    private commitStep(slot: PerimSlot, side: EdgeSide, size: number): string | null {
        const spec = this.elev.frameSpec;
        const id = stepOutlinePiece(spec, slot.stick.id, side, slot.leafIds, size);
        if(!id) {
            this.elev.draw.showError("Can't step this edge");
            this.relayout();
            return null;
        }

        spec.assignLiteMarks();
        spec.frameBlocksChanged.emit(true);

        const vs = this.elev.viewSettings;
        vs.selection.emit([{type: "stick", id, orientation: slot.mat.orientation}]);
        vs.activeTab.emit("selected");
        return id;
    }

    // give the one cell under the dragged outline piece a stick of its own (see
    // givePieceOwnStick). Returns the new stick's id (it goes on with the drag), or null.
    private commitOwnStick(slot: PerimSlot, side: EdgeSide): string | null {
        const spec = this.elev.frameSpec;
        const id = givePieceOwnStick(spec, slot.stick.id, side, slot.leafIds);
        if(!id) return null;

        spec.assignLiteMarks();
        spec.frameBlocksChanged.emit(true);

        const vs = this.elev.viewSettings;
        vs.selection.emit([{type: "stick", id, orientation: slot.mat.orientation}]);
        vs.activeTab.emit("selected");
        return id;
    }

    // grow the split part behind the dragged piece outward by `amount`, making the
    // row's fresh steps (see growSplitPart). Returns the stick's id (it goes on with the
    // drag), or null when the growth can't be made.
    private commitGrowOut(slot: PerimSlot, amount: number): string | null {
        const spec = this.elev.frameSpec;
        const result = growSplitPart(spec, slot.stick.id, amount);
        if(result !== "rebuilt" && result !== "changed") {
            this.elev.draw.showError("Can't step the frame for this size.");
            this.relayout();
            return null;
        }

        spec.assignLiteMarks();
        spec.frameBlocksChanged.emit(true);

        const vs = this.elev.viewSettings;
        vs.selection.emit([{type: "stick", id: slot.stick.id, orientation: slot.mat.orientation}]);
        vs.activeTab.emit("selected");
        return slot.stick.id;
    }

    // true when the child cell at `idx` is a step (a void).
    protected isStep(idx: number): boolean {
        return this.children[idx]?.model.kind === "void";
    }

    // the ids of every lite and panel cell in the grid under MIN_BLOCK on either axis.
    protected cellsUnderMin(): Set<string> {
        const out = new Set<string>();
        for(const item of this.scene.grid.getItems() as FrameBlockItem[]) {
            if(item.model.kind !== "lite" && item.model.kind !== "fixedPanel") continue;
            if(item.width.get() < MIN_BLOCK || item.height.get() < MIN_BLOCK) out.add(item.model.id);
        }
        return out;
    }

    // put the step stick in front of the void at `idx` back onto the frame edge: the
    // void goes, and the stick stays as the block's own edge stick (still split).
    // Returns that stick's id, or null when there was no step to remove.
    protected snapStepToEdge(idx: number): string | null {
        const cell = this.children[idx];
        const spec = this.elev.frameSpec;
        const stickId = cell ? Array.from(stepVoids(spec.frameBlocks).entries()).find(([, item]) => item.id === cell.model.id)?.[0] : undefined;
        if(!cell || !stickId || !removeStep(spec, cell.model.id)) {
            this.relayout();
            return null;
        }

        spec.assignLiteMarks();
        spec.frameBlocksChanged.emit(true);
        return stickId;
    }

    // record each step's size: a void reaches the outer frame line by layout, so the
    // step is the void's own extent.
    private recordSteps(spec: MdFrameSpec) {
        spec.stepInfo = new Map();

        for(const item of this.getItems()) {
            if(item.model.kind !== "void" || !item.layoutRect) continue;

            const v = item.layoutRect as LayoutRect;
            const vertical = item.parent.model.kind === "column";
            spec.stepInfo.set(item.model.id, {height: vertical ? v.height : v.width, extension: 0});
        }
    }

    // see TFrameBlock. Deepest wins: a block's own edge stick covers its part of the
    // edge even inside a part another stick covers.
    // A stick joined into another (see BlockOps.joinSticks) covers as that one, so the
    // two draw as one piece.
    collectEdgeLeaves(side: EdgeSide, cover: EdgeCover, out: EdgeLeaf[]) {
        const own = this.ownEdgeStick(side);
        const next: EdgeCover = own ? {stick: drawnStick(this.elev.frameSpec, own), owner: this} : cover;
        for(const c of this.cellsOnSide(side)) {
            c.collectEdgeLeaves(side, next, out);
        }
    }

    // this block's own stick on `side`: a stick child before its first cell or after
    // its last, on the edge across its flow.
    private ownEdgeStick(side: EdgeSide): MdFrameStick | undefined {
        if(this.model.kind === "row") {
            if(side === "left") return this.leadingEdgeStick;
            if(side === "right") return this.trailingEdgeStick;
            return undefined;
        }
        if(side === "top") return this.leadingEdgeStick;
        if(side === "bottom") return this.trailingEdgeStick;
        return undefined;
    }

    // the child cells touching `side`: the edge parallel to this block's main axis
    // is shared by every child (a row's top/bottom, a column's left/right); the
    // edge across the main axis is touched only by the first/last cell.
    cellsOnSide(side: EdgeSide): TFrameBlock[] {
        const cells = this.children;
        if(cells.length === 0) return [];

        const acrossMain = this.model.kind === "row"
            ? (side === "left" || side === "right")
            : (side === "top" || side === "bottom");

        if(!acrossMain) return cells;
        const leading = side === "top" || side === "left";
        return [leading ? cells[0] : cells[cells.length - 1]];
    }

    // ---- interior sticks beside a void -------------------------------------

    // see TFrameBlock.
    layoutVoidParts() {
        this.splitInteriorAtVoids();
        for(const c of this.children) c.layoutVoidParts();
    }

    // split each interior stick of this block at the voids beside it (see splitAtVoids).
    protected abstract splitInteriorAtVoids(): void;

    // draw an interior stick in pieces where a void lies beside it: a piece with a void
    // on one side faces a cell on one side only, and nothing is drawn where voids lie
    // on both sides. The first piece is the stick itself, so it keeps the model id;
    // each further piece has its own id, so it selects on its own.
    protected splitAtVoids(s: InteriorStick, leading: TFrameBlock, trailing: TFrameBlock) {
        const mat = s.stick;
        const vertical = mat.orientation === "vertical";
        const start = vertical ? mat.positionY : mat.positionX;
        const end = start + s.length.get();
        const cross = vertical ? mat.positionX : mat.positionY;

        const lead = voidSpans(leading, vertical ? "right" : "bottom");
        const trail = voidSpans(trailing, vertical ? "left" : "top");

        const cuts = new Set<number>([start, end]);
        for(const v of [...lead, ...trail]) {
            if(v.min > start && v.min < end) cuts.add(v.min);
            if(v.max > start && v.max < end) cuts.add(v.max);
        }
        // boundaries within the slop of each other are one cut (float noise between
        // the layouts of two voids), not a sliver piece. The stick's end wins.
        const points: number[] = [];
        for(const p of Array.from(cuts).sort((a, b) => a - b)) {
            const last = points[points.length - 1];
            if(last !== undefined && p - last <= EDGE_WIGGLE) {
                if(p === end) points[points.length - 1] = end;
                continue;
            }
            points.push(p);
        }

        // the visible pieces, merging neighbours with the same void state.
        const pieces: {min: number; max: number; state: string}[] = [];
        for(let i = 0; i < points.length - 1; i++) {
            const mid = (points[i] + points[i + 1]) / 2;
            const inLead = lead.some(v => mid > v.min && mid < v.max);
            const inTrail = trail.some(v => mid > v.min && mid < v.max);
            const state = `${inLead}:${inTrail}`;
            const last = pieces[pieces.length - 1];

            if(last && last.state === state && last.max === points[i]) {
                last.max = points[i + 1];
            } else {
                pieces.push({min: points[i], max: points[i + 1], state});
            }
        }
        const visible = pieces.filter(p => p.state !== "true:true");

        if(s.parts.length === 0) s.parts.push({mat, length: s.length});
        while(s.parts.length < visible.length) s.parts.push(s.makePart());

        s.parts.forEach((part, i) => {
            const p = visible[i];
            if(!p) {
                part.mat.graphic.visible = false;
                part.mat.voidSide = null;
                return;
            }

            part.mat.graphic.visible = true;
            part.mat.graphic.position.set(vertical ? cross : p.min, vertical ? p.min : cross);
            part.mat.modelEnds = {A: p.min === start, B: p.max === end};
            part.mat.voidSide = p.state === "true:false" ? (vertical ? "left" : "top")
                : p.state === "false:true" ? (vertical ? "right" : "bottom")
                : null;
            part.length.set(p.max - p.min, {debug: "void-split"});
        });
    }

    destroy() {
        this._destroyed = true;
        this.disposables.forEach(c => c.destroy());
        this.children.forEach(c => c.destroy());
        this.dimensionContainer.destroy();
    }

    private track<T extends Disposable>(input: T) {
        this.disposables.push(input);
        return input;
    }
}

// alignment slop when matching a cell against the frame edge.
const EDGE_WIGGLE = inches / 16;

// group an edge's leaves into pieces. Two pieces of different sticks meet at the
// centre of the gap between their cells (under the mullion there); a piece beside a
// void or the threshold stops at its last cell, so the stick there runs past it.
function edgePieces(side: EdgeSide, leaves: EdgeLeaf[]): EdgePiece[] {
    const pieces: EdgePiece[] = [];
    let prev: EdgePiece | null = null;

    for(const leaf of leaves) {
        const r = alongEdge(side, leaf.cell.layoutRect);
        if(!leaf.cover || !r) {
            prev = null;
            continue;
        }

        if(prev && prev.stick === leaf.cover.stick) {
            prev.max = r.max;
            prev.leaves.push(leaf.cell);
            continue;
        }

        const piece: EdgePiece = {stick: leaf.cover.stick, owner: leaf.cover.owner, min: r.min, max: r.max, leaves: [leaf.cell]};
        if(prev) {
            const mid = (prev.max + r.min) / 2;
            prev.max = mid;
            piece.min = mid;
        }
        pieces.push(piece);
        prev = piece;
    }

    return pieces;
}

// where a piece sits: in the slot its owner block gave the stick, on that block's
// `side`. null before the owner is laid out.
function pieceGeom(side: EdgeSide, p: EdgePiece, spec: MdFrameSpec): {x: number; y: number; length: number} | null {
    const rect = p.owner.layoutRect;
    if(!rect) return null;
    const span = alongEdge(side, rect)!;
    const t = crossThickness(stickThicknessVar(p.stick, spec).get(), span.max - span.min, pieceSlope(p.stick, side, span, span));
    const length = p.max - p.min;

    switch(side) {
        case "top": return {x: p.min, y: rect.y, length};
        case "bottom": return {x: p.min, y: rect.y + rect.height - t, length};
        case "left": return {x: rect.x, y: p.min, length};
        case "right": return {x: rect.x + rect.width - t, y: p.min, length};
    }
}

// the stick covering `side` of a cell at the corner nearest the cell's `end` along that
// side (its last leaf for "trailing", its first for "leading"), from the cell's own
// blocks only. null when a void or the threshold lies there, or no block inside the cell
// has a stick on that side.
function cornerCover(cell: TFrameBlock, side: EdgeSide, end: "leading" | "trailing"): EdgeCover {
    const leaves: EdgeLeaf[] = [];
    cell.collectEdgeLeaves(side, null, leaves);
    const leaf = end === "leading" ? leaves[0] : leaves[leaves.length - 1];
    return leaf?.cover ?? null;
}

// a laid-out rect's range along an edge (x for top/bottom, y for left/right).
function alongEdge(side: EdgeSide, r?: LayoutRect): {min: number; max: number} | null {
    if(!r) return null;
    return side === "top" || side === "bottom" ? {min: r.x, max: r.x + r.width} : {min: r.y, max: r.y + r.height};
}

// the ranges along `side` of a cell where a void lies against that side. A void that
// reaches an end of the cell runs on past it (it stretches out to the frame edge).
function voidSpans(cell: TFrameBlock, side: EdgeSide): {min: number; max: number}[] {
    const r = cell.layoutRect;
    const cellRange = alongEdge(side, r);
    if(!cellRange) return [];

    const leaves: EdgeLeaf[] = [];
    cell.collectEdgeLeaves(side, null, leaves);

    const out: {min: number; max: number}[] = [];
    for(const leaf of leaves) {
        if(leaf.cell.model.kind !== "void") continue;
        const v = alongEdge(side, leaf.cell.layoutRect);
        if(!v) continue;

        out.push({
            min: v.min <= cellRange.min + EDGE_WIGGLE ? -Infinity : v.min,
            max: v.max >= cellRange.max - EDGE_WIGGLE ? Infinity : v.max,
        });
    }
    return out;
}

// does this cell's subtree contain a door?
export function cellHasDoor(cell: TFrameBlock): boolean {
    return cell.getItems().some((i: any) => i?.model?.kind === "doors");
}

// model-level door check for use before the view children exist (constructor time).
function modelHasDoor(block: MdFrameBlock): boolean {
    return block.children.some(c =>
        c.kind === "item" ? c.item.kind === "doors" :
        c.kind === "block" ? modelHasDoor(c.block) : false);
}

// the axes a leaf cell draws an inside dimension on, given the block it sits in.
// A leaf dimensions only its flow axis — the one backed by the editable `size` var
// (width in a row, height in a column). The cross axis is solver-controlled (forced
// equal to the parent block), so drawing an editable dimension for it is misleading
// and a stray edit would pin a var the solver owns. A doors cell dimensions neither
// axis: both its width (innerWidth) and its height are calculated, so an edit here
// couldn't stick — DoorNominalDimensions dimensions the door's nominal sizes outside
// the frame, against the source vars. A void draws its own step dimension, out to the
// outer frame line (VoidFace). The threshold's height is thresholdHeight (Properties),
// not a cell dimension. A lite alone in a block that holds only it and its edge sticks
// (the wrapper that carries its sill or jamb) has no size of its own: the wrapper is
// dimensioned along its flow, and the other way the lite follows the block around it.
// With a step beside it the lite's size is its own again (the step takes the rest), so
// it is dimensioned then.
export function itemDimensionAxes(item: MdFrameItem, parent: MdFrameBlock): {width: boolean; height: boolean} {
    const wrapped = parent.children.every(c => c.kind === "stick" || (c.kind === "item" && c.item === item));
    if(item.kind === "void" || item.kind === "threshold" || wrapped) return {width: false, height: false};
    if(item.kind === "lite" || item.kind === "fixedPanel") return {width: parent.kind === "row", height: parent.kind === "column"};
    return {width: item.kind !== "doors", height: false};
}

// true when a block's extent on `axis` can already be read off its cells, so a
// dimension of its own would only repeat them: a block flowing along the axis whose
// cells every one dimension it (they sum to the block), or a block flowing across
// the axis with a cell that does (each cell matches the block). A nested block
// counts when the axis is its own flow axis (it is dimensioned, or its door is) or
// when its cells cover it in turn.
export function cellsDimension(block: MdFrameBlock, axis: "width" | "height"): boolean {
    const along = (axis === "width") === (block.kind === "row");
    const cells = block.children.filter(c => c.kind !== "stick");
    if(cells.length === 0) return false;

    const covered = (c: MdFrameBlockChild): boolean => {
        if(c.kind === "item") return itemDimensionAxes(c.item, block)[axis];
        if(c.kind === "block") return along || cellsDimension(c.block, axis);
        return false;
    };
    return along ? cells.every(covered) : cells.some(covered);
}
